import React from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  UnderlineButton,
} from '@tq/ui-components';

import { ConsultationDocument } from 'api';
import { useIsMobile, useThemedValueSwitch } from '@tq/ui-components/dist/hooks';
import { MenuOption } from 'types';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

export interface ActionMenuButtonProps {
  options: MenuOption[];
  consultationId?: number;
  document?: ConsultationDocument;
  label?: string;
  small?: boolean;
  darkContrast?: boolean;
}

export const ActionMenuButton: React.FC<ActionMenuButtonProps> = ({
  options,
  consultationId,
  document,
  label,
  small,
  darkContrast,
}) => {
  const { t } = useTranslation('home');
  const { tvs } = useThemedValueSwitch();

  const isMobile = useIsMobile();
  const buttonWidth = (): string => {
    if (isMobile) return '100% !important';
    return small ? '194px' : '248px';
  };

  const menuButtonTQStyles = tvs({
    tq: {
      height: '52px',
      borderColor: 'none',
      color: 'white',
      _active: {
        border: 'none',
        color: 'white',
        bgColor: '#0E3D44',
      },
    },
  });

  const tqMenuButtonComponent = darkContrast ? UnderlineButton : Button;

  return (
    <Menu>
      <MenuButton
        fontSize='md'
        minWidth={buttonWidth()}
        role={tvs({ ni: 'button' })}
        rightIcon={<FaChevronDown />}
        variant={tvs({ tq: 'secondary', ni: 'secondary' })}
        size={tvs({ ni: small ? 'sm' : 'md' })}
        borderColor={COLOURS.blue[600]}
        borderWidth={tvs({ ni: small ? 0 : 1 })}
        color={tvs({ tq: COLOURS.blue[600], ni: COLOURS.blue[600] })}
        height={tvs({ tq: '44px', ni: small ? '32px' : '44px' })}
        sx={tvs({ tq: darkContrast ? menuButtonTQStyles : {} })}
        as={tvs({ tq: () => tqMenuButtonComponent, ni: () => Button })}
      >
        {label ?? t('actions', 'Actions')}
      </MenuButton>
      <MenuList
        mt={0}
        overflowX={tvs({ ni: 'hidden' })}
        minWidth={buttonWidth()}
        width={buttonWidth()}
      >
        {options.map((option) => (
          <MenuItem
            key={option.label}
            lineHeight={tvs({ ni: small ? 1 : 2 })}
            onClick={() => option.onClick && option.onClick?.(consultationId, document)}
          >
            {option.iconRight ? (
              <Flex justifyContent='space-between' alignItems='center' w='100%'>
                {option.label}
                {option.icon && <Icon as={option.icon} />}
              </Flex>
            ) : (
              <Flex gridGap={2} alignItems='center'>
                {option.icon && <Icon as={option.icon} />}
                {option.label}
              </Flex>
            )}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
